import React, { FC, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ProductState } from '../../store/product/types';
import { getTotalPrice, getOptionLists } from '../../utils/optionList';
import { Configuration } from '../../store/configurator/types';
import numeral from 'numeral';
import 'numeral/locales/nl-nl';
import { useParams } from 'react-router-dom';
import { GlobalsState } from '../../store/globals/types'; 
import Modal from '../molecules/Modal';
import Button, { ButtonGroup } from '../atoms/Button';
import Cookies from 'js-cookie';

numeral.locale('nl-nl');

interface PriceProps {
  productId?: number;
  isOpen: boolean;
}

const Wrapper = styled.div<PriceProps>`
  font-size: 1.625rem;
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  transition: color 0.4s;

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${({ theme }) => theme.colors.secondary};
    `}

  @media (max-width: 800px) {
    order: 3;
  }
`;

const Select = styled.select<{ isOpen: boolean }>`
  background: none;
  border: none;
  outline:none;
  font-size: 1.625rem;
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  font-family: inherit;

  option{
    color: initial;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${({ theme }) => theme.colors.secondary};
    `}

  @media (max-width: 800px) {
    order: 3;
  }
`;

const Price: FC<PriceProps> = ({ productId, isOpen }) => {
  const { globals } = useSelector<RootState, GlobalsState>(({ globals }) => globals);
  const [selectVal, setSelectVal] = useState<string>('eur');

  const [showingModal, setShowingModal] = React.useState(false);

  // Redux:
  const product = useSelector<RootState, ProductState>(
    ({ product }) => product,
  );

  const id = productId ?? parseInt((useParams() as { id: string }).id);

  const configurations = useSelector<RootState, Configuration[]>(
    ({ configurator }) => configurator.configurations,
  );

  const { loading, error, optionLists } = getOptionLists(configurations, id);

  // Price's methods:
  const getBasePrice = () => {
    if (loading || error) {
      return 0;
    }

    return product.products.find((product) => product.id === id)?.price || 0;
  };

  const price = getTotalPrice(getBasePrice(), optionLists);

  const priceGbp = globals?.exchangeRateEURGBP != null ? price * globals?.exchangeRateEURGBP : null;

  const onCurrencyClick = (currency: string) => {
    Cookies.set('currency', currency);
    setSelectVal(currency);
    setShowingModal(false);
  };

  useEffect(() => {
    if (priceGbp != null) {
      const currency = Cookies.get('currency');
      if (currency) {
        setSelectVal(currency);
      } else {
        setShowingModal(true);
      }
    }
  }, [priceGbp]);

  const modal = (
    <>
      <Modal
        title="Munteenheid / Currency"
        open={showingModal}
        onHide={() => setShowingModal(false)}
      >
        <p>Welke munteenheid heeft je voorkeur? Dit kan later worden aangepast.</p>
        <p>What is your preferred currency unit? This can be changed later on.</p>
        <ButtonGroup>
          <Button onClick={() => onCurrencyClick('eur')}>€ Euro</Button>
          <Button onClick={() => onCurrencyClick('gbp')}>£ Pound</Button>
        </ButtonGroup>
      </Modal>
    </>
  );

  return <>
    {modal}
    {(priceGbp == null) && <Wrapper isOpen={isOpen}>{numeral(price).format('$0,0.00')}</Wrapper>}
    {(priceGbp != null) && <Select isOpen={isOpen} value={selectVal} onChange={(e) => setSelectVal(e.target.value)} >
      <option value="gbp">£&nbsp;{numeral(priceGbp).format('0,0.00')}</option>
      <option value="eur">€&nbsp;{numeral(price).format('0,0.00')}</option>
    </Select >}
  </>;
};

export default Price;
